import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Chip,
  Stack,
  IconButton,
  Slide
} from '@mui/material';
import {
  Close as CloseIcon,
  ExitToApp as ExitIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useUser } from '../../context/UserContext';
import { submitFeedback, FEEDBACK_TYPES } from '../../services/feedback';
import { trackExitIntent, trackEvent, getCurrentPage, getSessionStartTime } from '../../services/analytics';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Exit reasons with labels
const EXIT_REASONS = {
  'found_what_needed': 'I found what I was looking for',
  'too_complicated': 'The calculator is too complicated',
  'missing_info': 'I need more information to continue',
  'technical_issues': 'I encountered technical problems',
  'not_relevant': 'This tool is not relevant to me',
  'will_return_later': 'I will return later to finish',
  'other': 'Other reason'
};

const ExitIntentModal = ({
  enabled = true,
  sensitivity = 5, // pixels from top to trigger
  delay = 1000, // minimum time on page before showing
  cooldown = 300000, // 5 minutes cooldown between shows
  showOnce = true,
  onShow = null,
  onClose = null,
  onSubmit = null
}) => {
  const [open, setOpen] = useState(false);
  const [exitReason, setExitReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  
  const mouseLeaveDetected = useRef(false);
  const pageStartTime = useRef(Date.now());
  const lastShownTime = useRef(0);
  
  const { user } = useUser();

  // Check if we should show the modal
  const shouldShow = () => {
    if (!enabled || hasShown && showOnce) return false;
    
    const now = Date.now();
    const timeOnPage = now - pageStartTime.current;
    const timeSinceLastShown = now - lastShownTime.current;
    
    return timeOnPage >= delay && timeSinceLastShown >= cooldown;
  };

  // Mouse leave detection
  useEffect(() => {
    if (!enabled) return;

    const handleMouseLeave = (e) => {
      // Only trigger if mouse leaves from the top of the screen
      if (e.clientY <= sensitivity && shouldShow()) {
        if (!mouseLeaveDetected.current) {
          mouseLeaveDetected.current = true;
          showModal();
        }
      }
    };

    const handleMouseEnter = () => {
      mouseLeaveDetected.current = false;
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
    };
  }, [enabled, sensitivity, delay, cooldown, hasShown, showOnce]);

  // Mobile detection (long inactivity)
  useEffect(() => {
    if (!enabled) return;

    let inactivityTimer;
    let lastActivity = Date.now();

    const resetTimer = () => {
      lastActivity = Date.now();
      clearTimeout(inactivityTimer);
      
      // Show modal after 30 seconds of inactivity on mobile
      inactivityTimer = setTimeout(() => {
        if (shouldShow() && /Mobi|Android/i.test(navigator.userAgent)) {
          showModal();
        }
      }, 30000);
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });

    resetTimer();

    return () => {
      clearTimeout(inactivityTimer);
      events.forEach(event => {
        document.removeEventListener(event, resetTimer, true);
      });
    };
  }, [enabled]);

  const showModal = () => {
    if (!shouldShow()) return;

    setOpen(true);
    setHasShown(true);
    lastShownTime.current = Date.now();

    // Track exit intent
    const sessionDuration = getSessionStartTime() ? Date.now() - getSessionStartTime() : 0;
    trackExitIntent('mouse_leave', {
      page: getCurrentPage(),
      session_duration: sessionDuration,
      time_on_page: Date.now() - pageStartTime.current
    });

    if (onShow) {
      onShow();
    }
  };

  const handleClose = () => {
    setOpen(false);
    trackEvent('exit_intent_modal_closed', {
      page: getCurrentPage(),
      had_interaction: !!exitReason,
      reason: exitReason
    });

    if (onClose) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      const feedbackMessage = exitReason === 'other' 
        ? customReason 
        : EXIT_REASONS[exitReason] || '';

      if (feedbackMessage) {
        const result = await submitFeedback({
          feedbackType: FEEDBACK_TYPES.GENERAL,
          message: `Exit Intent: ${feedbackMessage}`,
          rating: null,
          userId: user?.uid || null,
          userEmail: email || user?.email || null
        });

        if (result.success) {
          trackEvent('exit_intent_feedback_submitted', {
            reason: exitReason,
            has_custom_reason: exitReason === 'other',
            has_email: !!(email || user?.email)
          });

          if (onSubmit) {
            onSubmit({ reason: exitReason, customReason, email });
          }
        }
      }

      setOpen(false);
    } catch (error) {
      console.error('[ExitIntentModal] Submit error:', error);
    }

    setSubmitting(false);
  };

  const handleReasonChange = (event) => {
    setExitReason(event.target.value);
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ExitIcon />
            <Typography variant="h6" component="div" color="inherit">
              Before you go...
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: 'white', color: 'text.primary', mx: 2, borderRadius: 2 }}>
        <Typography variant="body1" gutterBottom>
          We'd love to know what brought you here and how we can improve your experience.
        </Typography>

        <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
          <FormLabel component="legend" sx={{ mb: 1 }}>
            What's your main reason for leaving?
          </FormLabel>
          <RadioGroup value={exitReason} onChange={handleReasonChange}>
            {Object.entries(EXIT_REASONS).map(([key, label]) => (
              <FormControlLabel
                key={key}
                value={key}
                control={<Radio size="small" />}
                label={label}
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {/* Custom reason field */}
        {exitReason === 'other' && (
          <TextField
            fullWidth
            label="Please specify"
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            multiline
            rows={2}
            sx={{ mt: 2 }}
            placeholder="Tell us more about your reason..."
          />
        )}

        {/* Email field for follow-up */}
        {exitReason && exitReason !== 'found_what_needed' && (
          <TextField
            fullWidth
            label="Email (optional)"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="We'll follow up to help you succeed"
            helperText="We'll only use this to help you with the FERS calculator"
          />
        )}

        {/* Helpful chips */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Quick feedback:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label="Too complex"
              size="small"
              onClick={() => setExitReason('too_complicated')}
              color={exitReason === 'too_complicated' ? 'primary' : 'default'}
              variant={exitReason === 'too_complicated' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Missing info"
              size="small"
              onClick={() => setExitReason('missing_info')}
              color={exitReason === 'missing_info' ? 'primary' : 'default'}
              variant={exitReason === 'missing_info' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Found answer"
              size="small"
              onClick={() => setExitReason('found_what_needed')}
              color={exitReason === 'found_what_needed' ? 'success' : 'default'}
              variant={exitReason === 'found_what_needed' ? 'filled' : 'outlined'}
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} sx={{ color: 'white' }}>
          Skip
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!exitReason || submitting}
          startIcon={<SendIcon />}
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.3)'
            }
          }}
        >
          {submitting ? 'Sending...' : 'Send Feedback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExitIntentModal;